import { get, isBoolean, isEmpty, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';

import {
  Role,
  type TrialType,
  USER_DETAILS_QUERY,
  type User,
} from '@revelio/data-access';

import { getAuthStoreUser, useAuthStore } from './auth.repository';
import { getUserTrialType } from './helpers';
import {
  selectImpersonatedUser,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useImpersonationSelector,
} from './impersonation/impersonation.repository';
import { getImpersonationStateSnapshot } from './impersonation/impersonation.store';
import {
  applyImpersonatedUser,
  applyOverridesToUser,
} from './impersonation/impersonation.user-overrides';

export const useGetLoggedInUser = ({
  onTrialToLive,
}: {
  onTrialToLive?: () => void;
} = {}) => {
  const { ory, user, setUser } = useAuthStore();
  const loggedInUserId = ory?.id;
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const query = useQuery({
    query: USER_DETAILS_QUERY,
    variables: { ids: [loggedInUserId as string] },
    pause: !loggedInUserId, // maybe should be paused if already has user + they are live ???
  });

  const [result, reexecuteQuery] = query;
  const realLoggedInUser = useMemo(() => {
    const apiUser = result.data?.users?.[0];
    return (apiUser ?? user ?? {}) as User;
  }, [result.data, user]);
  const loggedInUser = useMemo(() => {
    if (isEmpty(realLoggedInUser)) {
      return realLoggedInUser;
    }

    if (isImpersonating && impersonatedUser) {
      return applyImpersonatedUser(realLoggedInUser, impersonatedUser);
    }

    if (overrides) {
      return applyOverridesToUser(realLoggedInUser, overrides);
    }

    return realLoggedInUser;
  }, [realLoggedInUser, isImpersonating, impersonatedUser, overrides]);

  useEffect(() => {
    if (isEmpty(realLoggedInUser)) {
      return;
    }

    // Prevent useless update which causes re-render
    if (isEqual(user, realLoggedInUser)) {
      return;
    }

    setUser(realLoggedInUser);
  }, [realLoggedInUser, setUser, user]);

  useEffect(() => {
    if (user?.live === false && realLoggedInUser.live === true) {
      onTrialToLive?.();
    }
  }, [user, realLoggedInUser, onTrialToLive]);

  const refetch = () => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  };
  return {
    query,
    loggedInUser,
    realLoggedInUser,
    currency: loggedInUser.currency_data,
    refetch,
  };
};

export const PREVIEW_TRIAL_COOKIE_NAME = 'previewTrialUser';
export const useUserTrialDetails = <T = null>({
  initialRenderValue,
}: {
  initialRenderValue?: boolean;
} = {}) => {
  const {
    loggedInUser,
    query: [{ fetching }],
  } = useGetLoggedInUser();

  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const [isTrialUser, setIsTrialUser] = useState<boolean | T>(
    isBoolean(initialRenderValue) ? initialRenderValue : (null as T)
  );
  const [trialType, setTrialType] = useState<TrialType>();
  const [isSelfSignupUser, setIsSelfSignupUser] = useState<boolean>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const isFetchingOrEmpty = fetching || isEmpty(loggedInUser);

    if (isFetchingOrEmpty) {
      return;
    }

    const isLiveUser = get(loggedInUser, 'live', false);
    const getClientName = get(
      loggedInUser,
      'client_name',
      'revelio_self_signup'
    );
    const trialType = getUserTrialType(loggedInUser);

    setIsTrialUser(!isLiveUser);
    trialType && setTrialType(trialType);
    setIsSelfSignupUser(getClientName === 'revelio_self_signup');
  }, [fetching, loggedInUser, isImpersonating, overrides]);

  return {
    isTrialUser,
    trialType,
    isSelfSignupUser,
    userEmail: loggedInUser.email,
  };
};

export const isRoleAdmin = (userRole: Role | undefined | null) =>
  userRole
    ? [Role.RevelioAdmin, Role.ClientAdmin, Role.SuperAdmin].includes(userRole)
    : false;

export const isRoleRevelioAdmin = (userRole: Role | undefined | null) =>
  userRole ? [Role.RevelioAdmin, Role.SuperAdmin].includes(userRole) : false;
export const useIsRevelioAdmin = () => {
  const {
    loggedInUser,
    query: [{ fetching }],
  } = useGetLoggedInUser();
  return {
    isRevelioAdmin: isRoleRevelioAdmin(loggedInUser.role),
    // set to this when no data fetched yet, useful when you don't want to show anything until you know the user role
    // avoiding things like flash renders
    dataRecieved: !isEqual(loggedInUser, {}),
    fetching,
  };
};

export const getLoggedInUserSnapshot = (): User | undefined => {
  const realUser = getAuthStoreUser();
  if (!realUser) {
    return undefined;
  }

  const { isImpersonating, impersonatedUser, overrides } =
    getImpersonationStateSnapshot();

  if (isImpersonating && impersonatedUser) {
    return applyImpersonatedUser(realUser, impersonatedUser);
  }

  if (overrides) {
    return applyOverridesToUser(realUser, overrides);
  }

  return realUser;
};
